import { pick, cloneDeep } from 'lodash'
import { nanoid } from '@reduxjs/toolkit'
import { ReduxQuoteInfo, ReduxRecapInfo } from '@src/types/apiType'

function createNewRecapCostLineAicp(costLine: ReduxRecapInfo) {
  const recapCostLine = pick(costLine, [
    'quoteId',
    'tenantId',
    'activeFlag',
    'categoryId',
    'category',
    'category_fr',
    'categoryPrefix',
    'subCategoryId',
    'subCategory',
    'subCategory_fr',
    'subCategoryPrefix',
    'subCategorySuffix',
    'isInternational',
    'editable',
    'costLineType',
    'costLineLabel',
    'language',
    'exchangeEnabled',
    'exchangeRate',
    'payrollTaxEnabled',
    'actualPayrollTaxEnabled',
    'payrollTaxType',
  ]) as ReduxRecapInfo
  // attributes related to custom costline
  recapCostLine.isCustomCostLine = true
  // reference to costline in recap info, which was used to create this costline
  // if the reference costLine already has a reference id, it means this line was created from an original costline
  // the same reference id is used on this new costline as well
  // if the reference costLine does not have a reference id, it means this is an original costline
  // we need to use this original costline's id as the referenceCostLineUuid
  recapCostLine.referenceCostLineUuid = costLine.referenceCostLineUuid
    ? costLine.referenceCostLineUuid
    : costLine.id

  recapCostLine.uuid = null
  recapCostLine.activeFlag = true
  recapCostLine.tableRowIndex = null
  recapCostLine.item = ''
  recapCostLine.item_fr = ''
  recapCostLine.rate = null
  recapCostLine.rateInEuros = null
  recapCostLine.quantity = null
  recapCostLine.total = 0
  recapCostLine.totalMargin = null

  recapCostLine.provisionalTotal = null
  recapCostLine.actualTotal = null
  recapCostLine.comments = ''
  recapCostLine.invoiceId = null
  recapCostLine.invoiceDocuments = []
  return recapCostLine
}

function createRecapCostLine(costLine: ReduxQuoteInfo) {
  const recapCostLine = pick(costLine, [
    'id',
    'categoryId',
    'category',
    'category_fr',
    'categoryPrefix',
    'subCategoryId',
    'subCategory',
    'subCategory_fr',
    'subCategoryPrefix',
    'subCategorySuffix',
    'item',
    'item_fr',
    'isInternational',
    'editable',
    'costLineType',
    'costLineLabel',
    'language',
    'rate',
    'exchangeEnabled',
    'exchangeRate',
    'rateInEuros',
    'quantity',
    'total',
    'totalMargin',
    'payrollTaxEnabled',
    'payrollTaxType',
  ]) as ReduxRecapInfo
  recapCostLine.uuid = recapCostLine.id
  recapCostLine.id = nanoid()
  recapCostLine.activeFlag = true
  recapCostLine.isCustomCostLine = false
  recapCostLine.referenceCostLineUuid = null
  recapCostLine.provisionalTotal = null
  recapCostLine.actualTotal = null
  recapCostLine.actualPayrollTaxEnabled = costLine.payrollTaxEnabled
  recapCostLine.comments = ''
  recapCostLine.invoiceId = null
  recapCostLine.invoiceDocuments = []
  return recapCostLine
}

function createRecapInfoCostLineIdsAndLabelsAicp(
  costLines: ReduxRecapInfo[],
  quoteId: string,
  tenantId: string | undefined,
  childFreelancers: string[]
) {
  let costLineLabelIndex = 100
  const costLineLabelSuffix = 'A'
  const costLineLabelSuffixInternational = 'B'
  return costLines.map((c, i) => {
    if (c.activeFlag) {
      c.tableRowIndex = i
      c.quoteId = quoteId
      c.tenantId = tenantId
      c.freelancers = ['dummyId']
      c.childFreelancers = [...childFreelancers]
      c.offlineIds = ['dummyId']
      if (c.costLineType === 'costLine') {
        c.costLineLabel = c.isInternational
          ? `${costLineLabelIndex}${costLineLabelSuffixInternational}`
          : `${costLineLabelIndex}${costLineLabelSuffix}`
        costLineLabelIndex++
      }
    }
    return c
  })
}

function createRecapInfoAicp(
  quoteInfo: ReduxQuoteInfo[],
  quoteId: string,
  tenantId: string | undefined,
  childFreelancers: string[]
) {
  if (!quoteInfo) return null
  let listOfVfxSubcategories = [] as string[]
  quoteInfo.map(row => {
    if (
      row.total &&
      row.total > 0 &&
      row.costLineType === 'subCategoryTotal' &&
      row.category === 'Visual Effects, Design & Animation, Interactive'
    ) {
      listOfVfxSubcategories.push(row.subCategory as string)
    }
  })
  const finalRecapInfo = quoteInfo.reduce((recapInfo: ReduxRecapInfo[], costLine) => {
    if (
      (costLine.total && costLine.total > 0) ||
      (costLine.costLineType === 'subCategoryDescription' &&
        listOfVfxSubcategories.includes(costLine.subCategory as string))
    ) {
      const recapCostLine = createRecapCostLine(costLine)
      recapInfo.push(recapCostLine)
    }
    return recapInfo
  }, [])
  return createRecapInfoCostLineIdsAndLabelsAicp(
    finalRecapInfo,
    quoteId,
    tenantId,
    childFreelancers
  )
}

function refreshRecapInfoAicp(
  quoteInfo: ReduxQuoteInfo[],
  recapInfo: ReduxRecapInfo[],
  quoteId: string,
  tenantId: string | undefined,
  childFreelancers: string[]
) {
  const oldRecapInfo = cloneDeep(recapInfo)
  // Checks if quoteInfo exists
  let listOfVfxSubcategories = [] as string[]
  quoteInfo.map(row => {
    if (
      row.total &&
      row.total > 0 &&
      row.costLineType === 'subCategoryTotal' &&
      row.category === 'Visual Effects, Design & Animation, Interactive'
    ) {
      listOfVfxSubcategories.push(row.subCategory as string)
    }
  })
  let newRecapInfo = quoteInfo.reduce((recapInfo: ReduxRecapInfo[], costLine) => {
    const oldCostLine = oldRecapInfo.find(val => val.uuid && val.uuid === costLine.id)
    if (
      (costLine.total && costLine.total > 0) ||
      (costLine.costLineType === 'subCategoryDescription' &&
        listOfVfxSubcategories.includes(costLine.subCategory as string))
    ) {
      let newCostLine
      if (oldCostLine) {
        const {
          item,
          item_fr,
          rateInEuros,
          rate,
          total,
          totalMargin,
          quantity,
          payrollTaxEnabled,
          payrollTaxType,
        } = costLine
        newCostLine = {
          ...oldCostLine,
          item,
          item_fr,
          rateInEuros,
          rate,
          total,
          totalMargin,
          quantity,
          payrollTaxEnabled,
          payrollTaxType,
          activeFlag: true,
        }
        recapInfo.push(newCostLine)
        // find the custom costlines that might have been created with this old cost line as a reference
        const customCostLinesWithOldCostLineAsReference = oldRecapInfo.filter(
          cl => cl.isCustomCostLine && cl.referenceCostLineUuid === oldCostLine.id
        )
        recapInfo = recapInfo.concat(customCostLinesWithOldCostLineAsReference)
      } else {
        newCostLine = createRecapCostLine(costLine)
        recapInfo.push(newCostLine)
      }
    } else if (
      oldCostLine &&
      (!costLine.total ||
        (costLine.costLineType === 'subCategoryDescription' &&
          !listOfVfxSubcategories.includes(costLine.subCategory as string)))
    ) {
      // * If oldRecapCostLine exists and budget costLine.total is 0 or null we set recap costline to total to 0
      const customCostLinesWithOldCostLineAsReference = oldRecapInfo.filter(
        cl => cl.isCustomCostLine && cl.referenceCostLineUuid === oldCostLine.id
      )
      const newCostLine = { ...oldCostLine, total: 0 }
      recapInfo.push(newCostLine)
      if (customCostLinesWithOldCostLineAsReference.length > 0) {
        recapInfo = recapInfo.concat(customCostLinesWithOldCostLineAsReference)
      }
    }
    return recapInfo
  }, [])
  return createRecapInfoCostLineIdsAndLabelsAicp(newRecapInfo, quoteId, tenantId, childFreelancers)
}

export {
  createRecapInfoAicp,
  refreshRecapInfoAicp,
  createNewRecapCostLineAicp,
  createRecapInfoCostLineIdsAndLabelsAicp,
}
